/**
 * useIdleDim — bedside-aware idle dim for the tablet (#138).
 *
 * Two modes determined by clock time:
 *
 *   Day   (07:00 – 22:00) : 60 s timeout, 72 % black overlay, tap-to-wake
 *   Night (22:00 – 07:00) : 25 s timeout, 97 % black overlay, soft clock,
 *                            long-press or double-tap to wake
 *
 * Post-call (when status transitions from any active state back to idle):
 *   Day   : 60 s before re-dim
 *   Night : 10 s before re-dim — assume the user wants to go back to sleep
 *
 * The hook itself only manages the timer + config; the actual overlay UI
 * (clock, wake gesture) lives in components/IdleDimOverlay.js.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Time-of-day boundary ───────────────────────────────────────────────────

export const DAY_START_HOUR = 7;     // 07:00 inclusive
export const NIGHT_START_HOUR = 22;  // 22:00 inclusive

export function isNightHour(date = new Date()) {
  const h = date.getHours();
  return h >= NIGHT_START_HOUR || h < DAY_START_HOUR;
}

// ── Per-mode config ────────────────────────────────────────────────────────

export const DAY_CONFIG = {
  mode: 'day',
  timeoutMs: 60_000,
  postCallTimeoutMs: 60_000,
  // 0.5 (was 0.72) — daytime dim is meant to be present-but-not-blackout;
  // 72% was too dark when tested on the tablet — content was barely
  // visible through the overlay. 50% reads as "dim" without obscuring
  // what's on screen. Night stays at 0.97 (essentially off).
  overlayOpacity: 0.5,
  showClock: false,
  // 'tap' = single short tap wakes; gesture handler is a no-op above this
  wakeGesture: 'tap',
};

export const NIGHT_CONFIG = {
  mode: 'night',
  timeoutMs: 25_000,
  postCallTimeoutMs: 10_000,
  overlayOpacity: 0.97,
  showClock: true,
  // 'longpress-or-doubletap' = single tap is ignored
  wakeGesture: 'longpress-or-doubletap',
};

export function getDimConfig(date = new Date()) {
  return isNightHour(date) ? NIGHT_CONFIG : DAY_CONFIG;
}

// ── Status sets ────────────────────────────────────────────────────────────

// Statuses that should suppress dimming entirely (active call/turn).
const ACTIVE_STATUSES = new Set([
  'connected', 'speaking', 'listening', 'calling', 'ringing',
  'transcribing', 'thinking',
]);

// ── The hook ───────────────────────────────────────────────────────────────

/**
 * @param {Object} args
 * @param {string} args.status - current call/turn status (drives active-suppression)
 * @returns {{ dimmed: boolean, config: object, wake: () => void }}
 */
export function useIdleDim({ status }) {
  const [dimmed, setDimmed] = useState(false);
  const [config, setConfig] = useState(() => getDimConfig());
  const timerRef = useRef(null);
  const statusRef = useRef(status);

  // Keep latest status visible inside the timer callback
  useEffect(() => { statusRef.current = status; }, [status]);

  const arm = useCallback((postCall = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setDimmed(false);
    const cfg = getDimConfig();
    setConfig(cfg);
    const ms = postCall ? cfg.postCallTimeoutMs : cfg.timeoutMs;
    timerRef.current = setTimeout(() => {
      if (ACTIVE_STATUSES.has(statusRef.current)) return;
      // Re-evaluate config at fire time in case the clock crossed the
      // day/night boundary while we were waiting.
      setConfig(getDimConfig());
      setDimmed(true);
    }, ms);
  }, []);

  // Mount + user-activity listener
  useEffect(() => {
    arm();
    const onActivity = () => arm();
    document.addEventListener('click', onActivity);
    document.addEventListener('touchstart', onActivity);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('click', onActivity);
      document.removeEventListener('touchstart', onActivity);
    };
  }, [arm]);

  // React to status transitions
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (ACTIVE_STATUSES.has(status)) {
      // Active → push the dim back
      arm();
    } else if (ACTIVE_STATUSES.has(prev) && !ACTIVE_STATUSES.has(status)) {
      // Just transitioned from active to idle: arm with the post-call timeout
      arm(true);
    }
  }, [status, arm]);

  return { dimmed, config, wake: () => arm() };
}
